import React, { useMemo, useState } from "react";
import { FiInfo, FiLock, FiSearch, FiUserMinus, FiUserPlus } from "react-icons/fi";
import { useAuth } from "../../../firebase/AuthContext";
import UserPickerModal from "./usageBilling/UserPickerModal";
import {
  groupRolesByArea,
  kindOf,
  KindBadge,
  SCOPED_AREAS,
} from "./RolesPanel";

const MEMBERS_PAGE_SIZE = 10;

// Area-by-area role checklist, used to create a group and to edit a group's
// roles. Same areas and labels as the Roles tab. Roles the editor doesn't hold
// stay visible but locked: nobody can grant or remove a role they don't have,
// and the backend refuses it too.
function RolePicker({ roles, selected, onChange, hasRole, language }) {
  const zh = language === "zh";
  // "all", or the key of one area.
  const [area, setArea] = useState("all");
  const [query, setQuery] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);

  const sections = useMemo(() => groupRolesByArea(roles), [roles]);
  const isOn = (role) => selected.includes(role.id);
  const label = (section) => (zh ? section.zh : section.en);
  const describe = (role) =>
    (zh && role.description_zh) || role.description || "";

  const q = query.trim().toLowerCase();
  const filtered = sections.map((section) => ({
    ...section,
    roles: section.roles.filter(
      (role) =>
        (!onlySelected || isOn(role)) &&
        (!q ||
          [section.en, section.zh, role.id, role.description, role.description_zh].some(
            (t) => (t || "").toLowerCase().includes(q),
          )),
    ),
  }));
  const shown = filtered.filter(
    (section) => section.roles.length && (area === "all" || section.key === area),
  );
  const hasLocked = roles.some((role) => !hasRole(role.id));

  const toggle = (role) => {
    if (!hasRole(role.id)) return;
    onChange(
      isOn(role)
        ? selected.filter((id) => id !== role.id)
        : [...selected, role.id],
    );
  };
  // Select all / clear only touch the roles the editor may change.
  const setAll = (sectionRoles, on) => {
    const editable = sectionRoles
      .filter((role) => hasRole(role.id))
      .map((role) => role.id);
    onChange(
      on
        ? [...new Set([...selected, ...editable])]
        : selected.filter((id) => !editable.includes(id)),
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="relative flex-1">
          <FiSearch
            className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value.trim()) setArea("all");
            }}
            placeholder={zh ? "搜尋角色…" : "Search roles…"}
            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={onlySelected}
            onChange={(e) => setOnlySelected(e.target.checked)}
            className="w-4 h-4 accent-orange-500"
          />
          {zh ? "只顯示已選擇" : "Selected only"}
        </label>
        <span className="self-start sm:self-auto px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 whitespace-nowrap">
          {zh
            ? `已選 ${selected.length}/${roles.length}`
            : `${selected.length} of ${roles.length} selected`}
        </span>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Area list */}
        <nav className="md:w-56 shrink-0 p-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto no-scrollbar md:max-h-[50vh] border-b md:border-b-0 md:border-r border-gray-200">
          {[{ key: "all", en: "All areas", zh: "全部區域", roles }, ...sections].map(
            (item) => {
              const active = area === item.key;
              const on = item.roles.filter(isOn).length;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setArea(item.key)}
                  className={`shrink-0 md:w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                    active
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="truncate">{label(item)}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${
                      on
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {on}/{item.roles.length}
                  </span>
                </button>
              );
            },
          )}
        </nav>

        {/* Roles */}
        <div className="flex-1 min-w-0 md:max-h-[50vh] overflow-y-auto no-scrollbar">
          {SCOPED_AREAS.includes(area) && (
            <div className="mx-4 mt-3 flex gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              <FiInfo className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span>
                {zh
                  ? "view_own 與 view_all 決定成員能存取哪些項目：僅限自己建立的，或全部。其他角色決定能對這些項目做什麼。"
                  : "view_own and view_all decide which ones a member can reach: only those they created, or all of them. The other roles decide what they can do there."}
              </span>
            </div>
          )}

          {shown.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-500">
              {zh ? "沒有符合的角色" : "No roles to show"}
            </p>
          ) : (
            shown.map((section) => (
              <div key={section.key}>
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-y border-gray-200">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {label(section)}
                    </p>
                    {area !== "all" && (
                      <p className="text-xs text-gray-400 truncate">
                        {zh ? section.hintZh : section.hintEn}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs font-medium">
                    <button
                      type="button"
                      onClick={() => setAll(section.roles, true)}
                      className="text-orange-600 hover:underline"
                    >
                      {zh ? "全選" : "Select all"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAll(section.roles, false)}
                      className="text-gray-500 hover:underline"
                    >
                      {zh ? "清除" : "Clear"}
                    </button>
                  </div>
                </div>
                <ul className="divide-y divide-gray-200">
                  {section.roles.map((role) => {
                    const editable = hasRole(role.id);
                    return (
                      <li key={role.id}>
                        <label
                          title={
                            editable
                              ? undefined
                              : zh
                                ? "你沒有這個角色，因此無法指派或移除"
                                : "You don't hold this role, so you can't grant or remove it"
                          }
                          className={`flex items-start gap-3 px-4 py-3 ${
                            editable
                              ? "cursor-pointer hover:bg-gray-50"
                              : "cursor-not-allowed opacity-60"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isOn(role)}
                            disabled={!editable}
                            onChange={() => toggle(role)}
                            className="mt-0.5 w-4 h-4 accent-orange-500 shrink-0"
                          />
                          <KindBadge kindKey={kindOf(role.id)} language={language} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-700">
                              {describe(role) || (
                                <span className="italic text-gray-400">
                                  {zh ? "尚無說明" : "No description"}
                                </span>
                              )}
                            </p>
                            <code className="text-xs text-gray-400 font-mono">
                              {role.id}
                            </code>
                          </div>
                          {!editable && (
                            <FiLock
                              className="w-4 h-4 mt-0.5 text-gray-400 shrink-0"
                              aria-hidden="true"
                            />
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      {hasLocked && (
        <p className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          <FiLock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {zh
            ? "鎖定的角色是你自己沒有的，無法指派或移除。"
            : "Locked roles are ones you don't hold yourself, so you can't grant or remove them."}
        </p>
      )}
    </div>
  );
}

// A group's roles, read-only, grouped by area: "Website chatbot 3/10" and
// the role ids it holds there, each with its description on hover.
function GroupRoleSummary({ roles, groupRoleIds, language }) {
  const zh = language === "zh";
  const sections = useMemo(() => groupRolesByArea(roles), [roles]);
  const known = new Set(roles.map((role) => role.id));
  const unknown = groupRoleIds.filter((id) => !known.has(id));
  const rows = sections
    .map((section) => ({
      ...section,
      held: section.roles.filter((role) => groupRoleIds.includes(role.id)),
    }))
    .filter((section) => section.held.length);

  if (!rows.length && !unknown.length) {
    return (
      <p className="text-sm italic text-gray-500">
        {zh ? "暫無分配角色" : "No roles assigned"}
      </p>
    );
  }

  return (
    <div className="divide-y divide-gray-200 border border-gray-200 rounded-xl bg-white">
      {rows.map((section) => (
        <div
          key={section.key}
          className="flex flex-col sm:flex-row sm:items-start gap-2 px-4 py-3"
        >
          <div className="sm:w-56 shrink-0 flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              {zh ? section.zh : section.en}
            </span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 tabular-nums">
              {section.held.length}/{section.roles.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {section.held.map((role) => (
              <span
                key={role.id}
                title={(zh && role.description_zh) || role.description}
                className="inline-flex px-2 py-0.5 rounded-md text-xs font-mono bg-gray-100 text-gray-700"
              >
                {role.id}
              </span>
            ))}
          </div>
        </div>
      ))}
      {unknown.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-start gap-2 px-4 py-3">
          <span className="sm:w-56 shrink-0 text-sm font-medium text-gray-900">
            {zh ? "未知角色" : "Unknown roles"}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {unknown.map((id) => (
              <span
                key={id}
                className="inline-flex px-2 py-0.5 rounded-md text-xs font-mono bg-amber-100 text-amber-800"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GroupsPanel({
  hasRole,
  groups,
  roles,
  language,
  onCreateGroup,
  onDeleteGroup,
  fetchGroupDetails,
  updateGroupRoles,
  onUpdateGroups,
}) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [groupDetails, setGroupDetails] = useState(null);
  const [groupDetailsLoading, setGroupDetailsLoading] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [groupRole, setGroupRole] = useState([]);

  // Create group form state
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupRoles, setNewGroupRoles] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  // Adding and removing members in bulk, in the group details window. Every
  // account belongs to exactly one group, so adding someone moves them out of
  // their current group and removing someone moves them to "user".
  const { currentUser } = useAuth();
  const canMoveMembers = hasRole("users.assign_group");
  const [selectedMembers, setSelectedMembers] = useState(() => new Set());
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [membersBusy, setMembersBusy] = useState(false);
  const [membersNotice, setMembersNotice] = useState(null); // { tone, text }
  const [showAddMembers, setShowAddMembers] = useState(false);

  const pageMembers = groupDetails?.membersData || [];
  const pageAllSelected =
    pageMembers.length > 0 &&
    pageMembers.every((member) => selectedMembers.has(member.uid));

  const handleSaveRole = async (group_id, roles) => {
    if (!updateGroupRoles) return;

    setRoleUpdateLoading(true);
    try {
      // Stays open on failure, so the selection isn't lost.
      const saved = await updateGroupRoles(group_id, roles);
      if (saved) {
        // The group's details are still open underneath; keep them current.
        setGroupRole(roles);
        setSelectedGroup((group) => (group ? { ...group, roles } : group));
        setShowRoleModal(false);
      }
    } catch (error) {
      console.error("Error updating group roles:", error);
    } finally {
      setRoleUpdateLoading(false);
    }
  };

  const openRoleModal = (group) => {
    setSelectedGroup(group);
    setSelectedRoles(group.roles || []);
    setShowRoleModal(true);
  };

  const openEditModal = async (group) => {
    setSelectedMembers(new Set());
    setConfirmRemove(false);
    setMembersNotice(null);
    setShowEditModal(true);
    setSelectedGroup(group);
    setGroupDetailsLoading(true);
    setGroupDetails(null);
    setMembersPage(1);
    try {
      const groupDetails = await fetchGroupDetails(
        group.id,
        1,
        MEMBERS_PAGE_SIZE,
      );
      setGroupDetails(groupDetails);
      setGroupRole(groupDetails.roles || []);
      // Initialize selectedRoles from group roles so checkboxes reflect current state
      setSelectedRoles(groupDetails.roles || []);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      setGroupDetails(null);
    } finally {
      setGroupDetailsLoading(false);
    }
  };

  const handleMembersPageChange = async (newPage) => {
    if (!selectedGroup || groupDetailsLoading || newPage < 1) return;

    setGroupDetailsLoading(true);
    try {
      const details = await fetchGroupDetails(
        selectedGroup.id,
        newPage,
        MEMBERS_PAGE_SIZE,
      );
      if (details) {
        setGroupDetails(details);
        setMembersPage(newPage);
      }
    } catch (error) {
      console.error("Failed to fetch group members page:", error);
    } finally {
      setGroupDetailsLoading(false);
    }
  };

  const toggleMember = (uid) =>
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });

  // Selection is kept across pages; this ticks or clears the page shown.
  const toggleMembersPage = () =>
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      pageMembers.forEach((member) =>
        pageAllSelected ? next.delete(member.uid) : next.add(member.uid),
      );
      return next;
    });

  // After a move: reload this page of members (the first page, if this one
  // emptied) and start the selection over.
  const refreshMembers = async () => {
    setSelectedMembers(new Set());
    setConfirmRemove(false);
    const details = await fetchGroupDetails(
      selectedGroup.id,
      membersPage,
      MEMBERS_PAGE_SIZE,
    );
    if (details && !details.membersData?.length && membersPage > 1) {
      setMembersPage(1);
      setGroupDetails(
        await fetchGroupDetails(selectedGroup.id, 1, MEMBERS_PAGE_SIZE),
      );
    } else if (details) {
      setGroupDetails(details);
    }
  };

  const failureNotice = (failed) => ({
    tone: "error",
    text:
      language === "zh"
        ? `${failed.length} 位未能移動：${failed[0].error}`
        : `${failed.length} couldn't be moved: ${failed[0].error}`,
  });

  const removeSelectedMembers = async () => {
    setMembersBusy(true);
    setMembersNotice(null);
    try {
      const { moved, failed } = await onUpdateGroups(
        [...selectedMembers],
        "user",
      );
      await refreshMembers();
      setMembersNotice(
        failed.length
          ? failureNotice(failed)
          : {
              tone: "ok",
              text:
                language === "zh"
                  ? `已將 ${moved.length} 位移到 User`
                  : `Moved ${moved.length} to User`,
            },
      );
    } finally {
      setMembersBusy(false);
    }
  };

  // The picker keeps whoever failed ticked, and closes when nobody did.
  const addMembers = async (uids) => {
    const result = await onUpdateGroups(uids, selectedGroup.id);
    await refreshMembers();
    if (result.moved.length) {
      setMembersNotice({
        tone: "ok",
        text:
          language === "zh"
            ? `已新增 ${result.moved.length} 位成員`
            : `Added ${result.moved.length} member${result.moved.length === 1 ? "" : "s"}`,
      });
    }
    return result;
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert(language === "zh" ? "請輸入群組名稱" : "Please enter group name");
      return;
    }

    setCreateLoading(true);
    try {
      const success = await onCreateGroup({
        name: newGroupName.trim(),
        description: newGroupDescription.trim(),
        roles: newGroupRoles,
      });

      if (success) {
        // Reset form
        setNewGroupName("");
        setNewGroupDescription("");
        setNewGroupRoles([]);
        setShowCreateModal(false);
      }
    } catch (error) {
      console.error("Error creating group:", error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    setDeleteLoading(true);
    try {
      const success = await onDeleteGroup(selectedGroup.id);
      if (success) {
        setShowDeleteConfirm(false);
        setShowEditModal(false);
        setSelectedGroup(null);
      }
    } catch (error) {
      console.error("Error deleting group:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="h-full">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {language === "zh" ? "使用者群組管理" : "User Groups Management"}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {language === "zh"
                ? "管理用戶組及其角色權限"
                : "Manage user groups and their role permissions"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500">
              {groups.length} {language === "zh" ? "個群組" : "groups"}
            </div>
            {hasRole("groups.manage") && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                {language === "zh" ? "建立群組" : "Create Group"}
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {groups.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-16 w-16 text-gray-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.196-2.121M9 20H4v-2a3 3 0 015.196-2.121m4.414-5.586a4 4 0 11-5.657 0L12 14l5.657-5.586zM9 7a4 4 0 118 0v0a4 4 0 01-8 0v0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {language === "zh" ? "暫無用戶群組" : "No groups found"}
              </h3>
              <p className="text-gray-500 max-w-sm mx-auto">
                {language === "zh"
                  ? "系統中還沒有創建任何用戶群組。"
                  : "No user groups have been created in the system yet."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer"
                  onClick={() => openEditModal(group)}
                >
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {language === "zh" ? "查看詳情" : "View Details"}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                        {group.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {group.name}
                        </h4>
                        <p className="text-sm text-gray-500 line-clamp-2">
                          {group.description ||
                            (language === "zh" ? "無描述" : "No description")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                          />
                        </svg>
                        <span className="font-medium">
                          {Array.isArray(group.members)
                            ? group.members.length
                            : 0}
                        </span>
                        <span className="ml-1">
                          {language === "zh" ? "成員" : "members"}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <span className="font-medium">
                          {Array.isArray(group.roles) ? group.roles.length : 0}
                        </span>
                        <span className="ml-1">
                          {language === "zh" ? "角色" : "roles"}
                        </span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                          {language === "zh"
                            ? "點擊查看詳情"
                            : "Click to view details"}
                        </span>
                        <svg
                          className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Group Details Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowEditModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                  {groupDetails?.name?.charAt(0).toUpperCase() ||
                    selectedGroup?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {groupDetails?.name || selectedGroup?.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {groupDetails?.description ||
                      selectedGroup?.description ||
                      (language === "zh" ? "無描述" : "No description")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Group Roles Section */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {language === "zh"
                          ? "群組角色權限"
                          : "Group Role Permissions"}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {language === "zh"
                          ? "管理此群組的角色和權限"
                          : "Manage roles and permissions for this group"}
                      </p>
                    </div>
                  </div>

                  {hasRole("groups.assign_roles") && (
                    <button
                      onClick={() => openRoleModal(selectedGroup)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all transform hover:scale-105 shadow-sm hover:shadow-md"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      {language === "zh" ? "編輯角色" : "Edit Roles"}
                    </button>
                  )}
                </div>

                <GroupRoleSummary
                  roles={roles}
                  groupRoleIds={groupRole || []}
                  language={language}
                />
              </div>

              {/* Group Members Section */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {language === "zh" ? "群組成員" : "Group Members"}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {groupDetails?.membersPagination?.totalMembers ??
                          groupDetails?.membersData?.length ??
                          0}{" "}
                        {language === "zh" ? "位成員" : "members"}
                      </p>
                    </div>
                  </div>
                  {canMoveMembers && hasRole("users.read") && (
                    <button
                      onClick={() => setShowAddMembers(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-orange-600 border border-orange-200 bg-white hover:bg-orange-50"
                    >
                      <FiUserPlus className="w-4 h-4" aria-hidden="true" />
                      {language === "zh" ? "新增成員" : "Add members"}
                    </button>
                  )}
                </div>

                <div className="p-6">
                  {membersNotice && (
                    <div
                      className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                        membersNotice.tone === "error"
                          ? "border-red-200 bg-red-50 text-red-700"
                          : "border-green-200 bg-green-50 text-green-700"
                      }`}
                    >
                      {membersNotice.text}
                    </div>
                  )}

                  {canMoveMembers && selectedMembers.size > 0 && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                      <div>
                        <span className="font-medium">
                          {language === "zh"
                            ? `已選 ${selectedMembers.size} 位`
                            : `${selectedMembers.size} selected`}
                        </span>
                        {selectedMembers.has(currentUser?.uid) && (
                          <span className="block text-xs">
                            {language === "zh"
                              ? "包含你自己：移出後你會失去此群組的權限。"
                              : "Includes you: you'll lose this group's access."}
                          </span>
                        )}
                      </div>
                      {selectedGroup?.id === "user" ? (
                        <span className="text-xs">
                          {language === "zh"
                            ? "每個帳號都需要一個群組。要把人移出 User，請到其他群組新增他們。"
                            : "Everyone needs a group. To take people out of User, add them to another group."}
                        </span>
                      ) : confirmRemove ? (
                        <div className="flex items-center gap-2">
                          <span>
                            {language === "zh"
                              ? `將 ${selectedMembers.size} 位移到 User？`
                              : `Move ${selectedMembers.size} to User?`}
                          </span>
                          <button
                            onClick={removeSelectedMembers}
                            disabled={membersBusy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50"
                          >
                            {membersBusy && (
                              <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            )}
                            {language === "zh" ? "確認" : "Confirm"}
                          </button>
                          <button
                            onClick={() => setConfirmRemove(false)}
                            disabled={membersBusy}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {language === "zh" ? "取消" : "Cancel"}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemove(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50"
                        >
                          <FiUserMinus className="w-4 h-4" aria-hidden="true" />
                          {language === "zh" ? "移出群組" : "Remove from group"}
                        </button>
                      )}
                    </div>
                  )}

                  {groupDetailsLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <div className="flex gap-1">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
                        <div
                          className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                          style={{
                            animationDelay: "0.1s",
                          }}
                        ></div>
                        <div
                          className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                          style={{
                            animationDelay: "0.2s",
                          }}
                        ></div>
                      </div>
                      <span className="text-blue-600 text-lg font-medium">
                        {language === "zh"
                          ? "正在加载成員..."
                          : "Loading members..."}
                      </span>
                    </div>
                  ) : groupDetails?.membersData &&
                    groupDetails.membersData.length > 0 ? (
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {canMoveMembers && (
                              <th className="pl-6 pr-2 py-3 w-10">
                                <input
                                  type="checkbox"
                                  checked={pageAllSelected}
                                  onChange={toggleMembersPage}
                                  className="w-4 h-4 accent-orange-500"
                                  aria-label={
                                    language === "zh"
                                      ? "選擇本頁所有成員"
                                      : "Select everyone on this page"
                                  }
                                />
                              </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              UID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {language === "zh" ? "姓名" : "Name"}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {language === "zh" ? "狀態" : "Status"}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {groupDetails.membersData.map((member, index) => (
                            <tr
                              key={member.uid || member.id || `member-${index}`}
                              className={`transition-colors ${
                                selectedMembers.has(member.uid)
                                  ? "bg-orange-50"
                                  : "hover:bg-gray-50"
                              }`}
                            >
                              {canMoveMembers && (
                                <td className="pl-6 pr-2 py-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedMembers.has(member.uid)}
                                    onChange={() => toggleMember(member.uid)}
                                    className="w-4 h-4 accent-orange-500"
                                    aria-label={member.email || member.uid}
                                  />
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm text-gray-900 font-mono bg-gray-50">
                                {member.uid}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                    {member.displayName
                                      ?.charAt(0)
                                      .toUpperCase() || "?"}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {member.displayName || (
                                        <span className="italic text-gray-400">
                                          {language === "zh"
                                            ? "無姓名"
                                            : "No Name"}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {member.email || (
                                  <span className="italic text-gray-400">
                                    {language === "zh" ? "無信箱" : "No Email"}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {language === "zh" ? "活躍" : "Active"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}

                  {groupDetails?.membersPagination &&
                    groupDetails.membersPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between px-2 pt-4">
                        <p className="text-sm text-gray-500">
                          {language === "zh"
                            ? `第 ${groupDetails.membersPagination.page} 頁，共 ${groupDetails.membersPagination.totalPages} 頁 (${groupDetails.membersPagination.totalMembers} 位成員)`
                            : `Page ${groupDetails.membersPagination.page} of ${groupDetails.membersPagination.totalPages} (${groupDetails.membersPagination.totalMembers} members)`}
                        </p>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              handleMembersPageChange(membersPage - 1)
                            }
                            disabled={
                              !groupDetails.membersPagination.hasPrev ||
                              groupDetailsLoading
                            }
                            className="p-2 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>

                          {Array.from(
                            {
                              length: Math.min(
                                groupDetails.membersPagination.totalPages,
                                7,
                              ),
                            },
                            (_, i) => {
                              const { page, totalPages } =
                                groupDetails.membersPagination;
                              // Show pages around the current page
                              let p;
                              if (totalPages <= 7) {
                                p = i + 1;
                              } else if (page <= 4) {
                                p = i + 1;
                              } else if (page >= totalPages - 3) {
                                p = totalPages - 6 + i;
                              } else {
                                p = page - 3 + i;
                              }
                              return (
                                <button
                                  key={p}
                                  onClick={() => handleMembersPageChange(p)}
                                  disabled={groupDetailsLoading}
                                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                    p === page
                                      ? "bg-blue-500 text-white shadow-sm"
                                      : "text-gray-600 hover:bg-blue-50 hover:text-blue-500"
                                  }`}
                                >
                                  {p}
                                </button>
                              );
                            },
                          )}

                          <button
                            onClick={() =>
                              handleMembersPageChange(membersPage + 1)
                            }
                            disabled={
                              !groupDetails.membersPagination.hasNext ||
                              groupDetailsLoading
                            }
                            className="p-2 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                  {!groupDetailsLoading &&
                    (!groupDetails?.membersData ||
                      groupDetails.membersData.length === 0) && (
                      <div className="text-center py-12">
                        <svg
                          className="mx-auto h-16 w-16 text-gray-300 mb-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                          />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {language === "zh" ? "暫無成員" : "No members"}
                        </h3>
                        <p className="text-gray-500 max-w-sm mx-auto">
                          {language === "zh"
                            ? "此組中還沒有任何成員。"
                            : "This group doesn't have any members yet."}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
              {/* {hasRole("admin") &&
                            selectedGroup?.id !== "user" ? (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-all"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    {language === "zh"
                                        ? "刪除群組"
                                        : "Delete Group"}
                                </button>
                            ) : ( */}
              <div></div>
              {/* )} */}
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                {language === "zh" ? "關閉" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRoleModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {language === "zh" ? "编辑组角色" : "Edit Group Roles"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === "zh"
                      ? `為 "${selectedGroup?.name}" 群組指派角色權限`
                      : `Assign role permissions for "${selectedGroup?.name}" group`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRoleModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <RolePicker
                roles={roles}
                selected={selectedRoles}
                onChange={setSelectedRoles}
                hasRole={hasRole}
                language={language}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
              <p className="text-xs text-gray-400">
                {language === "zh"
                  ? `已選擇 ${selectedRoles.length} 個角色`
                  : `${selectedRoles.length} roles selected`}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                >
                  {language === "zh" ? "取消" : "Cancel"}
                </button>
                <button
                  onClick={() =>
                    handleSaveRole(selectedGroup.id, selectedRoles)
                  }
                  disabled={roleUpdateLoading || selectedRoles.length === 0}
                  className={`px-5 py-2 rounded-xl text-sm font-medium transition-all ${
                    roleUpdateLoading || selectedRoles.length === 0
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600 text-white shadow-sm"
                  }`}
                >
                  {roleUpdateLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                      {language === "zh" ? "儲存中..." : "Saving..."}
                    </div>
                  ) : (
                    <>{language === "zh" ? "儲存變更" : "Save Changes"}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowCreateModal(false);
              setNewGroupName("");
              setNewGroupDescription("");
              setNewGroupRoles([]);
            }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {language === "zh" ? "建立新群組" : "Create New Group"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === "zh"
                      ? "設定群組名稱、描述和角色權限"
                      : "Set group name, description and role permissions"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewGroupName("");
                  setNewGroupDescription("");
                  setNewGroupRoles([]);
                }}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Group Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    {language === "zh" ? "群組名稱" : "Group Name"}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    placeholder={
                      language === "zh" ? "輸入群組名稱" : "Enter group name"
                    }
                  />
                </div>

                {/* Group Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    {language === "zh" ? "群組描述" : "Group Description"}
                  </label>
                  <textarea
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
                    rows="3"
                    placeholder={
                      language === "zh"
                        ? "輸入群組描述（可選）"
                        : "Enter group description (optional)"
                    }
                  />
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    {language === "zh" ? "指派角色" : "Assign Roles"}
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    {language === "zh"
                      ? "選擇要分配給此群組的角色（可選）"
                      : "Select roles to assign to this group (optional)"}
                  </p>

                  <RolePicker
                    roles={roles}
                    selected={newGroupRoles}
                    onChange={setNewGroupRoles}
                    hasRole={hasRole}
                    language={language}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewGroupName("");
                  setNewGroupDescription("");
                  setNewGroupRoles([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                {language === "zh" ? "取消" : "Cancel"}
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={createLoading || !newGroupName.trim()}
                className={`px-5 py-2 rounded-xl text-sm font-medium transition-all ${
                  createLoading || !newGroupName.trim()
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600 text-white shadow-sm"
                }`}
              >
                {createLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                    {language === "zh" ? "建立中..." : "Creating..."}
                  </div>
                ) : (
                  <>{language === "zh" ? "建立群組" : "Create Group"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !deleteLoading && setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {language === "zh" ? "確認刪除" : "Confirm Deletion"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === "zh"
                      ? "此操作無法撤銷"
                      : "This action cannot be undone"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all disabled:opacity-50"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                {language === "zh" ? (
                  <>
                    您確定要刪除群組{" "}
                    <span className="font-bold text-gray-900">
                      "{selectedGroup?.name}"
                    </span>{" "}
                    嗎？
                  </>
                ) : (
                  <>
                    Are you sure you want to delete the group{" "}
                    <span className="font-bold text-gray-900">
                      "{selectedGroup?.name}"
                    </span>
                    ?
                  </>
                )}
              </p>

              {groupDetails?.membersData &&
                groupDetails.membersData.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-amber-800">
                          {language === "zh"
                            ? "警告：此群組有成員"
                            : "Warning: This group has members"}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {language === "zh"
                            ? `此群組目前有 ${groupDetails.membersData.length} 位成員。刪除後，這些成員將失去群組關聯。`
                            : `This group currently has ${groupDetails.membersData.length} member(s). Deleting it will remove their group association.`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              <p className="text-xs text-gray-500 leading-relaxed">
                {language === "zh"
                  ? "刪除後，所有與此群組相關的權限配置將被移除。"
                  : "All permission configurations associated with this group will be removed."}
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                {language === "zh" ? "取消" : "Cancel"}
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={deleteLoading}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                    {language === "zh" ? "刪除中..." : "Deleting..."}
                  </div>
                ) : (
                  <>{language === "zh" ? "確認刪除" : "Confirm Delete"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add members: the shared user picker, in multi-select mode */}
      {showAddMembers && selectedGroup && (
        <UserPickerModal
          multiple
          hideGroupId={selectedGroup.id}
          title={
            language === "zh"
              ? `新增成員到「${selectedGroup.name || selectedGroup.id}」`
              : `Add members to ${selectedGroup.name || selectedGroup.id}`
          }
          subtitle={
            language === "zh"
              ? "每個帳號只屬於一個群組，新增後會從目前的群組移出。新權限會在對方的登入更新後生效（一小時內）。"
              : "Everyone belongs to one group, so adding people here moves them out of their current one. Their new access starts when their sign-in refreshes, within the hour."
          }
          confirmLabel={(n) =>
            language === "zh"
              ? `新增 ${n} 位`
              : `Add ${n} member${n === 1 ? "" : "s"}`
          }
          onConfirm={addMembers}
          onClose={() => setShowAddMembers(false)}
          language={language}
        />
      )}
    </div>
  );
}
